import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import styles from "./Legales.module.css";
import LegalesArticle from "./components/LegalesArticle/LegalesArticle";
import LegalesSEO from "../../components/SEO/LegalesSEO"; // Import the new SEO component
import {
  getLegalArticleByUrl,
  getLegalArticlesByCategory,
  categoryToTabMap,
  tabToCategoryMap,
  LegalArticle,
} from "../../lib/getLegalArticles";

const Legales: React.FC = () => {
  const { articleUrl } = useParams<{ articleUrl?: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>("terms");
  const [selectedArticle, setSelectedArticle] = useState<LegalArticle | null>(
    null
  );
  const [articles, setArticles] = useState<LegalArticle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Reset state when component unmounts or when URL changes dramatically
  useEffect(() => {
    return () => {
      // Cleanup function
      setSelectedArticle(null);
      setArticles([]);
      setError(null);
    };
  }, []); // Empty dependency array means this runs on unmount

  // Handle URL params on component mount and when articleUrl changes
  useEffect(() => {
    const loadArticleFromUrl = async () => {
      setLoading(true);
      setError(null);

      console.log(`Processing URL parameter: ${articleUrl}`);

      if (articleUrl) {
        // Check if it's a main tab URL
        const tabMapping: Record<string, string> = {
          "terminos-y-condiciones": "terms",
          "politica-de-privacidad": "privacy",
          "politica-de-envios": "shipping",
          "politica-cambios-devoluciones": "returns",
          promociones: "promotions",
        };

        if (tabMapping[articleUrl]) {
          // It's a main tab URL
          console.log(`Setting active tab to: ${tabMapping[articleUrl]}`);
          setActiveTab(tabMapping[articleUrl]);
          setSelectedArticle(null);
        } else {
          // It's a specific article URL, try to load it
          try {
            console.log(`Loading specific article with URL: ${articleUrl}`);
            const article = await getLegalArticleByUrl(articleUrl);

            if (article) {
              console.log(
                "Article successfully loaded, setting selected article:",
                {
                  id: article.id,
                  title: article.title,
                  category: article.category,
                  url: article.url,
                }
              );
              setSelectedArticle(article);

              // Set active tab based on article category
              const tab = categoryToTabMap[article.category];
              if (tab) {
                console.log(
                  `Setting active tab based on article category: ${tab}`
                );
                setActiveTab(tab);
              } else {
                console.warn(
                  `Unknown category: ${article.category}, defaulting to terms`
                );
                setActiveTab("terms");
              }
            } else {
              // If URL doesn't match any content, redirect to the main legal page
              console.error("Article not found:", articleUrl);
              setError(`No se encontró el artículo: ${articleUrl}`);
              navigate("/legales", { replace: true });
            }
          } catch (error) {
            console.error("Error loading article from URL:", error);
            setError(
              `Error al cargar el artículo: ${
                error instanceof Error ? error.message : String(error)
              }`
            );
            // Don't navigate away automatically - let the user see the error
          }
        }
      } else {
        // No URL parameter, default to terms
        console.log("No URL parameter, defaulting to terms tab");
        setActiveTab("terms");
        setSelectedArticle(null);
      }

      setLoading(false);
    };

    loadArticleFromUrl();
  }, [articleUrl, navigate]);

  // Load articles based on active tab - separate from the URL effect
  useEffect(() => {
    // Skip loading if we're already loading a specific article from URL
    if (loading && articleUrl && articleUrl !== "promociones") {
      console.log(
        "Skipping tab-based article loading because URL parameters are being processed"
      );
      return;
    }

    const loadArticles = async () => {
      // Don't reload if we have a selected article already from the URL
      if (selectedArticle && articleUrl && articleUrl !== "promociones") {
        console.log(
          "Skipping tab-based article loading because an article is already selected"
        );
        return;
      }

      setLoading(true);
      console.log(`Loading articles for active tab: ${activeTab}`);

      if (activeTab === "promotions") {
        // For promotions tab, load all promotion articles
        try {
          console.log("Loading promotion articles");
          const promotionArticles = await getLegalArticlesByCategory(
            tabToCategoryMap[activeTab]
          );
          console.log(`Loaded ${promotionArticles.length} promotion articles`);
          setArticles(promotionArticles);
          // Only clear selection if we're on the main promotions view
          if (articleUrl === "promociones" || !articleUrl) {
            setSelectedArticle(null);
          }
        } catch (error) {
          console.error("Failed to load promotion articles:", error);
          setArticles([]);
          setError("Error al cargar las promociones");
        }
      } else {
        // For other tabs, load the main article for that category
        try {
          console.log(
            `Loading articles for category: ${tabToCategoryMap[activeTab]}`
          );
          const categoryArticles = await getLegalArticlesByCategory(
            tabToCategoryMap[activeTab]
          );
          if (categoryArticles.length > 0) {
            console.log(
              "Setting first article as selected:",
              categoryArticles[0].title
            );
            setSelectedArticle(categoryArticles[0]);
          } else {
            console.warn(
              `No articles found for category: ${tabToCategoryMap[activeTab]}`
            );
            setSelectedArticle(null);
            setError(
              `No hay contenido disponible para ${tabToCategoryMap[activeTab]}`
            );
          }
        } catch (error) {
          console.error(`Failed to load articles for ${activeTab}:`, error);
          setError(
            `Error al cargar el contenido: ${
              error instanceof Error ? error.message : String(error)
            }`
          );
        }
      }

      setLoading(false);
    };

    loadArticles();
  }, [activeTab, articleUrl]); // Include articleUrl to ensure this updates when URL changes

  // Handle tab changes with URL updates
  const handleTabChange = (tab: string) => {
    console.log(`Tab changed to: ${tab}`);
    setActiveTab(tab);
    setSelectedArticle(null);
    setError(null);

    // Map tabs to URL paths
    const tabToUrlMap: Record<string, string> = {
      terms: "terminos-y-condiciones",
      privacy: "politica-de-privacidad",
      shipping: "politica-de-envios",
      returns: "politica-cambios-devoluciones",
      promotions: "promociones",
    };

    if (tabToUrlMap[tab]) {
      navigate(`/legales/${tabToUrlMap[tab]}`, { replace: true });
    } else {
      navigate("/legales", { replace: true });
    }
  };

  // Handle article selection with URL updates
  const handleArticleSelect = (article: LegalArticle) => {
    console.log("Selected article:", article.title);
    if (article.url) {
      navigate(`/legales/${article.url}`);
    }
  };

  // Render content based on active tab and selected article
  const renderContent = () => {
    if (loading) {
      return <p>Cargando contenido...</p>;
    }

    if (error) {
      return (
        <div className={styles.errorMessage}>
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      );
    }

    // If a specific article is selected
    if (selectedArticle) {
      console.log("Rendering selected article:", selectedArticle.title, {
        category: selectedArticle.category,
        fullContentExists: Boolean(selectedArticle.fullContent),
        activeTab,
      });

      return (
        <LegalesArticle
          key={selectedArticle.id}
          title={selectedArticle.title}
          content={selectedArticle.fullContent || ""}
          coverImage={selectedArticle.image?.url}
          date={selectedArticle.date}
          duration={selectedArticle.duration}
          backLink={activeTab === "promotions"} // Only show back link for promotions
          onBack={() => {
            // Handle back button click
            navigate("/legales/promociones");
          }}
        />
      );
    }

    // If viewing promotions list
    if (activeTab === "promotions") {
      return (
        <>
          <h2>Promociones y Ofertas</h2>
          <p>
            A continuación encontrarás los términos y condiciones de nuestras
            promociones vigentes. Haz clic en cada promoción para ver los
            detalles completos.
          </p>

          <div className={styles.promotionsGrid}>
            {articles.length === 0 ? (
              <p>No hay promociones actualmente.</p>
            ) : (
              articles.map((promo) => (
                <div key={promo.id} className={styles.promotionCard}>
                  {promo.image && (
                    <img
                      src={promo.image.url}
                      alt={promo.title}
                      className={styles.promoCoverImage}
                    />
                  )}
                  <div className={styles.promoContent}>
                    <h3 className={styles.promoTitle}>{promo.title}</h3>
                    {promo.duration && (
                      <span className={styles.promoDate}>{promo.duration}</span>
                    )}
                    <p className={styles.promoDescription}>
                      {promo.description}
                    </p>
                    <div
                      className={styles.readMoreButton}
                      onClick={() => handleArticleSelect(promo)}
                    >
                      Ver términos completos
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      );
    }

    // For other tabs waiting for article
    return <p>Cargando contenido...</p>;
  };

  return (
    <div className="container">
      <LegalesSEO activeTab={activeTab} selectedArticle={selectedArticle} />
      <div className={styles.legalesContainer}>
        <header className={styles.legalesHeader} role="banner">
          <h1 className={styles.legalesTitle}>Información Legal</h1>
          <p className={styles.legalesSubtitle}>
            Conoce nuestras políticas y términos legales para asegurar una
            experiencia transparente y segura en nuestra tienda.
          </p>
        </header>

        <nav
          className={styles.tabsContainer}
          role="tablist"
          aria-label="Secciones legales"
        >
          <Link
            to="/legales/terminos-y-condiciones"
            className={`${styles.tabItem} ${
              activeTab === "terms" ? styles.active : ""
            }`}
            role="tab"
            aria-selected={activeTab === "terms"}
            aria-current={activeTab === "terms" ? "page" : undefined}
            tabIndex={0}
            aria-label="Términos y Condiciones"
            onClick={(e) => {
              e.preventDefault();
              handleTabChange("terms");
            }}
          >
            Términos y Condiciones
          </Link>
          <Link
            to="/legales/politica-de-privacidad"
            className={`${styles.tabItem} ${
              activeTab === "privacy" ? styles.active : ""
            }`}
            role="tab"
            aria-selected={activeTab === "privacy"}
            aria-current={activeTab === "privacy" ? "page" : undefined}
            tabIndex={0}
            aria-label="Política de Privacidad"
            onClick={(e) => {
              e.preventDefault();
              handleTabChange("privacy");
            }}
          >
            Política de Privacidad
          </Link>
          <Link
            to="/legales/politica-de-envios"
            className={`${styles.tabItem} ${
              activeTab === "shipping" ? styles.active : ""
            }`}
            role="tab"
            aria-selected={activeTab === "shipping"}
            aria-current={activeTab === "shipping" ? "page" : undefined}
            tabIndex={0}
            aria-label="Política de Envíos"
            onClick={(e) => {
              e.preventDefault();
              handleTabChange("shipping");
            }}
          >
            Política de Envíos
          </Link>
          <Link
            to="/legales/politica-cambios-devoluciones"
            className={`${styles.tabItem} ${
              activeTab === "returns" ? styles.active : ""
            }`}
            role="tab"
            aria-selected={activeTab === "returns"}
            aria-current={activeTab === "returns" ? "page" : undefined}
            tabIndex={0}
            aria-label="Cambios y Devoluciones"
            onClick={(e) => {
              e.preventDefault();
              handleTabChange("returns");
            }}
          >
            Cambios y Devoluciones
          </Link>
          <Link
            to="/legales/promociones"
            className={`${styles.tabItem} ${
              activeTab === "promotions" ? styles.active : ""
            }`}
            role="tab"
            aria-selected={activeTab === "promotions"}
            aria-current={activeTab === "promotions" ? "page" : undefined}
            tabIndex={0}
            aria-label="Términos de Promociones"
            onClick={(e) => {
              e.preventDefault();
              handleTabChange("promotions");
            }}
          >
            Términos de Promociones
          </Link>
        </nav>

        <main
          className={styles.legalContent}
          role="main"
          aria-label="Contenido legal"
        >
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default Legales;
